import React, { useState, useCallback } from 'react';
import { filingApi, FilingHistoryItem, FilingHistoryPage } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { fmtDate, fmtUSD as fmt } from '../../utils/fmt';
import { useAsyncResource } from '../../hooks/useAsyncResource';

type HistoryStatus = FilingHistoryItem['status'];

interface StatusCfg { label: string; bg: string; border: string; color: string; }
const STATUS_CFG: Record<HistoryStatus, StatusCfg> = {
  pending:    { label: 'Pending',    bg: '#F8FAFC', border: '#E2E8F0', color: '#64748B' },
  ready:      { label: 'Ready',      bg: '#EFF6FF', border: '#BFDBFE', color: '#1D4ED8' },
  approved:   { label: 'Approved',   bg: '#F0FDF4', border: '#BBF7D0', color: '#15803D' },
  downloaded: { label: 'Downloaded', bg: '#F5F3FF', border: '#DDD6FE', color: '#6D28D9' },
  filed:      { label: 'Filed',      bg: '#ECFDF5', border: '#6EE7B7', color: '#047857' },
};

const TH: React.CSSProperties = { fontSize: 10, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.05em' };

function StatusBadge({ status }: { status: HistoryStatus }): JSX.Element {
  const c = STATUS_CFG[status];
  return (
    <span style={{ background: c.bg, border: `1px solid ${c.border}`, color: c.color, borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 600 }}>
      {c.label}
    </span>
  );
}

function AmendBadge(): JSX.Element {
  return (
    <span style={{ background: '#FFF7ED', border: '1px solid #FED7AA', color: '#C2410C', borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 700, marginLeft: 6, letterSpacing: '.02em' }}>
      AMEND
    </span>
  );
}

function SkRow(): JSX.Element {
  return (
    <div style={{ display: 'flex', alignItems: 'center', padding: '12px 18px', borderBottom: '1px solid #F1F5F9' }}>
      {[160, 110, 100, 110, 110, 0].map((w, i) => (
        <div key={i} className="skeleton-box" style={{ flex: w ? `0 0 ${w}px` : 1, height: 12, marginRight: i < 5 ? 10 : 0 }} />
      ))}
      <div style={{ width: 60 }} />
    </div>
  );
}

interface FilingHistoryProps {
  onReviewFiling: (stateCode: string, stateName: string, period: string) => void;
}

export default function FilingHistory({ onReviewFiling }: FilingHistoryProps): JSX.Element {
  const { organizationId } = useAuth();
  const [page, setPage] = useState(1);

  const fetcher = useCallback(
    () => filingApi.getHistory(organizationId, page),
    [organizationId, page]
  );
  const { data, loading, error, reload } = useAsyncResource<FilingHistoryPage | null>(fetcher, null);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const pages = data?.pages ?? 1;

  if (error) return (
    <div style={{ background: '#FFF1F2', border: '1px solid #FECDD3', borderRadius: 10, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ fontSize: 13, color: '#BE123C', flex: 1 }}>{error}</span>
      <button onClick={reload} style={{ padding: '6px 14px', background: '#0F172A', color: '#fff', border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Retry</button>
    </div>
  );

  return (
    <div>
      {!loading && (
        <div style={{ fontSize: 11, color: '#94A3B8', marginBottom: 10 }}>
          {total} filing{total !== 1 ? 's' : ''} total
        </div>
      )}

      <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', background: '#F8FAFC', padding: '10px 18px', borderBottom: '1px solid #E2E8F0' }}>
          <span style={{ flex: '0 0 160px', ...TH }}>State</span>
          <span style={{ flex: '0 0 110px', ...TH }}>Period</span>
          <span style={{ flex: '0 0 100px', ...TH }}>Frequency</span>
          <span style={{ flex: '0 0 110px', ...TH }}>Status</span>
          <span style={{ flex: '0 0 110px', textAlign: 'right', ...TH }}>Tax Due</span>
          <span style={{ flex: 1, textAlign: 'right', ...TH }}>Approved</span>
          <span style={{ width: 60 }} />
        </div>

        {loading
          ? Array.from({ length: 8 }).map((_, i) => <SkRow key={i} />)
          : items.length === 0
            ? <div style={{ padding: 40, textAlign: 'center', color: '#94A3B8', fontSize: 13 }}>No filing history yet.</div>
            : items.map(item => <HistoryRow key={item.filing_id} item={item} onReview={onReviewFiling} />)
        }
      </div>

      {pages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: 14 }}>
          <button
            onClick={() => setPage(p => p - 1)} disabled={page <= 1}
            style={{ padding: '5px 14px', fontSize: 12, fontWeight: 600, borderRadius: 7, border: '1px solid #E2E8F0', background: page <= 1 ? '#F8FAFC' : '#fff', color: page <= 1 ? '#CBD5E1' : '#0F172A', cursor: page <= 1 ? 'default' : 'pointer' }}
          >
            ← Prev
          </button>
          <span style={{ fontSize: 12, color: '#64748B' }}>Page {page} of {pages}</span>
          <button
            onClick={() => setPage(p => p + 1)} disabled={page >= pages}
            style={{ padding: '5px 14px', fontSize: 12, fontWeight: 600, borderRadius: 7, border: '1px solid #E2E8F0', background: page >= pages ? '#F8FAFC' : '#fff', color: page >= pages ? '#CBD5E1' : '#0F172A', cursor: page >= pages ? 'default' : 'pointer' }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

interface HistoryRowProps {
  item: FilingHistoryItem;
  onReview: (stateCode: string, stateName: string, period: string) => void;
}
function HistoryRow({ item, onReview }: HistoryRowProps): JSX.Element {
  const [hov, setHov] = useState(false);
  const approvedLabel = item.approved_by
    ? `${item.approved_by} · ${fmtDate(item.approved_at)}`
    : fmtDate(item.approved_at);

  return (
    <div style={{ display: 'flex', alignItems: 'center', padding: '12px 18px', borderBottom: '1px solid #F1F5F9', background: '#fff' }}>
      <div style={{ flex: '0 0 160px' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#0F172A' }}>{item.state_name}</span>
        {item.is_amendment && <AmendBadge />}
        {item.amendments_filing_id && !item.is_amendment && (
          <span style={{ fontSize: 10, color: '#94A3B8', marginLeft: 6 }}>amended</span>
        )}
      </div>
      <span style={{ flex: '0 0 110px', fontSize: 12, color: '#475569' }}>{item.period}</span>
      <span style={{ flex: '0 0 100px', fontSize: 12, color: '#64748B', textTransform: 'capitalize' }}>
        {item.frequency ?? '—'}
      </span>
      <div style={{ flex: '0 0 110px' }}><StatusBadge status={item.status} /></div>
      <span style={{ flex: '0 0 110px', textAlign: 'right', fontSize: 13, fontWeight: 700, color: '#0F172A' }}>
        {fmt(item.tax_due)}
      </span>
      <span style={{ flex: 1, textAlign: 'right', fontSize: 11, color: '#64748B' }}>{approvedLabel}</span>
      <div style={{ width: 60, display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={() => onReview(item.state_code, item.state_name, item.period)}
          onMouseEnter={() => setHov(true)}
          onMouseLeave={() => setHov(false)}
          style={{ background: hov ? '#EFF6FF' : 'none', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#1B4F8C', padding: '3px 8px', borderRadius: 5, transition: 'background 0.1s' }}
        >
          View →
        </button>
      </div>
    </div>
  );
}
